/**
 * Detects whether text content looks like markdown by checking for common patterns.
 * Returns true if multiple markdown indicators are found, suggesting intentional markdown.
 */
export function looksLikeMarkdown(text: string): boolean {
    if (!text || text.length < 3) return false;
    const patterns = [
        /^#{1,6}\s+/m,           // headings: # Heading
        /\*\*.+?\*\*/,           // bold: **text**
        /(?:^|\s)\*.+?\*(?:\s|$)/,  // italic: *text*
        /^\s*[-*+]\s+/m,        // unordered list: - item
        /^\s*\d+\.\s+/m,        // ordered list: 1. item
        /\[.+?\]\(.+?\)/,       // links: [text](url)
        /^>\s+/m,               // blockquote: > text
        /`[^`]+`/,              // inline code: `code`
        /^```/m,                // code fence: ```
    ];
    const matches = patterns.filter(p => p.test(text)).length;
    return matches >= 2;
}

/**
 * Creates a TinyMCE setup handler that:
 * - Cmd+V: detects markdown in clipboard and forces conversion via markdown plugin
 * - Cmd+Shift+V: pastes as plain text, bypassing markdown conversion
 */
export function createMarkdownPasteSetup() {
    return (editor) => {
        let skipMarkdownConversion = false;
        let capturedPlainText: string | null = null;

        editor.on("keydown", (e) => {
            if ((e.metaKey || e.ctrlKey) && e.shiftKey && (e.key === "v" || e.key === "V")) {
                skipMarkdownConversion = true;
            }
        });

        editor.on("paste", (e) => {
            const plainText = e.clipboardData?.getData("text/plain");
            const htmlText = e.clipboardData?.getData("text/html");

            if (skipMarkdownConversion) {
                capturedPlainText = plainText || null;
            } else if (htmlText && plainText && looksLikeMarkdown(plainText)) {
                capturedPlainText = plainText;
            }
        });

        editor.on("PastePreProcess", (args) => {
            const shouldSkip = skipMarkdownConversion;
            skipMarkdownConversion = false;

            if (capturedPlainText == null) return;
            const text = capturedPlainText;
            capturedPlainText = null;

            args.content = "";

            setTimeout(() => {
                if (shouldSkip) {
                    const html = text
                        .split(/\n\n+/)
                        .map(p => "<p>" + p.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/\n/g, "<br>") + "</p>")
                        .join("");
                    editor.insertContent(html);
                } else {
                    editor.execCommand("mceInsertClipboardContent", false, { text });
                }
            }, 0);
        });
    };
}
